import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, ArrowRight } from 'lucide-react';
import { MENTORS as ALL_MENTORS } from '../data/mentors';

// Show the first 8 mentors on the strip; the full bench lives on /mentors.
const MENTORS = ALL_MENTORS.slice(0, 8);

export function MentorStrip({
  eyebrow = 'Meet the mentors',
  headline = 'Current Ivy League students,\u00A0',
  headlineItalic = 'on your side.',
  subtext = 'Anonymized snapshots from our active mentor bench — matched to each client by major, target schools, and background. Coast to coast in the U.S. and around the world.',
  bg = 'light', // 'light' | 'cream' | 'dark'
  testid = 'mentor-strip',
}) {
  const isDark = bg === 'dark';
  const sectionBg = bg === 'cream' ? 'bg-[#F9F9F7] border-y border-[#E2E2E2]' : isDark ? 'bg-[#0F392B]' : 'bg-white';
  const eyebrowColor = 'text-[#D4AF37]';
  const headlineColor = isDark ? 'text-white' : 'text-[#0F392B]';
  const subColor = isDark ? 'text-white/70' : 'text-muted-foreground';
  const cardBg = isDark
    ? 'bg-white/5 border-white/10 backdrop-blur-sm'
    : 'bg-white border-[#E2E2E2] hover:shadow-lg';
  const cardMeta = isDark ? 'text-white/60' : 'text-muted-foreground';
  const cardTitle = isDark ? 'text-white' : 'text-[#0F392B]';
  const badgeBg = isDark ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-[#0F392B] text-[#D4AF37]';

  return (
    <section className={`py-16 md:py-20 ${sectionBg}`} data-testid={testid}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <p className={`text-sm font-medium ${eyebrowColor} tracking-wide uppercase mb-3`}>{eyebrow}</p>
          <h2 className={`text-2xl md:text-3xl font-medium tracking-tight ${headlineColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            {headline}<span className="italic text-[#D4AF37]">{headlineItalic}</span>
          </h2>
          <p className={`text-sm ${subColor} mt-3 max-w-2xl mx-auto leading-relaxed`}>
            {subtext}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {MENTORS.map((m, i) => (
            <div
              key={i}
              className={`rounded-2xl border p-5 transition-all duration-300 ${cardBg}`}
              data-testid={`${testid}-card-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full ${badgeBg} flex items-center justify-center text-[11px] font-bold tracking-wide`}>
                  {m.initials}
                </div>
                <div className={`text-[10px] uppercase tracking-widest font-medium ${eyebrowColor}`}>Mentor</div>
              </div>
              <p className={`text-sm font-semibold ${cardTitle} leading-tight`} style={{ fontFamily: "'Playfair Display', serif" }}>
                {m.school}
              </p>
              <p className={`text-xs ${cardMeta} mt-1`}>{m.major}</p>
              <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#E2E2E2]'} flex items-center gap-1.5`}>
                <Globe className={`h-3 w-3 ${isDark ? 'text-white/40' : 'text-muted-foreground'}`} strokeWidth={1.75} />
                <p className={`text-[11px] ${cardMeta}`}>{m.hometown}</p>
              </div>
            </div>
          ))}
        </div>

        <p className={`text-[11px] ${subColor} text-center mt-8 italic`}>
          Mentor identities are anonymized to protect current-student privacy. Full profiles are shared with clients during matching.
        </p>

        <div className="mt-8 text-center">
          <Link
            to="/mentors"
            className={`inline-flex items-center gap-2 text-sm font-medium border-b transition-colors ${
              isDark
                ? 'text-[#D4AF37] hover:text-white border-[#D4AF37]/40 hover:border-white/60'
                : 'text-[#0F392B] hover:text-[#0F392B]/70 border-[#0F392B]/30 hover:border-[#0F392B]/70'
            }`}
            data-testid={`${testid}-see-all`}
          >
            See the full mentor bench <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
